import { useCallback } from "react";
import type { AssetTypeDefinition } from "@hightouch/lib/llm/shared";
import type {
  AssetBaseType,
  AssetGroupSyncKind,
} from "@hightouch/lib/agents/asset-base-types";
import {
  type AssetCollectionsQuery,
  useAssetCollectionsQuery,
} from "src/graphql/types";

/**
 * The workspace's asset collections, read from `amp_asset_groups` via the
 * `assetCollections` resolver.
 *
 * This replaced `useAgentSettings().campaignAgent.assetTypes`, which read the
 * legacy `agentic_settings.settings` JSON blob. The blob is no longer written,
 * so anything still reading it would silently go stale.
 */
export type AssetCollection = AssetTypeDefinition & {
  brandProfileIds: string[];
  system: boolean;
  /** What feeds the collection; null for an uploaded one. */
  syncKind: AssetGroupSyncKind | null;
  assetCount: number;
};

type RawAssetCollection = AssetCollectionsQuery["assetCollections"][number];

/**
 * Narrows the wire shape once, at the boundary. `baseType` is a nullable
 * `String` server-side; downstream code consumes the branded
 * `AssetBaseType | null`, where `null` means the collection accepts any kind.
 * The resolver validates it on write, so this is a cast rather than a filter.
 */
function toAssetCollection(raw: RawAssetCollection): AssetCollection {
  return {
    id: raw.id,
    name: raw.name,
    baseType: raw.baseType as AssetBaseType | null,
    instructions: raw.instructions,
    system: raw.system,
    syncKind: raw.syncKind ?? null,
    assetCount: raw.assetCount,
    brandProfileIds: raw.brandProfileIds,
    alwaysInjectIntoContext: raw.alwaysInjectIntoContext,
    ...(raw.imageGuardrails != null
      ? { imageGuardrails: raw.imageGuardrails }
      : {}),
  };
}

export const useAssetCollections = ({
  suspense = true,
}: {
  suspense?: boolean;
} = {}): {
  collections: AssetCollection[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
} => {
  // `useCallback` keeps `select`'s identity stable so React Query's result
  // cache isn't defeated by a fresh array each render.
  const select = useCallback(
    (data: AssetCollectionsQuery) =>
      data.assetCollections.map(toAssetCollection),
    [],
  );
  // Most callers suspend; modal callers opt out so their shell can stay open
  // while the collection list loads or retries.
  const { data, isLoading, error, refetch } = useAssetCollectionsQuery(
    {},
    { suspense, select },
  );

  return {
    collections: data ?? [],
    isLoading,
    error: error ? String(error) : null,
    retry: () => void refetch(),
  };
};
